/**
 * ScheduleOverridesTab — Master Data
 * Displays and manages schedule overrides for a school.
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6
 */

import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventBusy as HolidayIcon,
  WbSunny as HalfDayIcon,
  CalendarViewWeek as AltSatIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getScheduleOverridesDirect, deleteScheduleOverrideDirect } from '@timetable/api';
import type { ScheduleOverride, OverrideType } from '@timetable/types';
import { useAuthStore } from '../../store/authStore';
import { queryKeys } from '../../lib/queryKeys';
import { ConfirmDialog } from '../ConfirmDialog';
import { ScheduleOverrideFormDialog } from './ScheduleOverrideFormDialog';

// ── Helpers ───────────────────────────────────────────────────────────────────

const OVERRIDE_TYPE_LABELS: Record<OverrideType, string> = {
  holiday: 'Holiday',
  half_day_schedule: 'Half Day',
  alternate_saturday: 'Alt. Saturday',
};

const OVERRIDE_TYPE_COLORS: Record<OverrideType, 'error' | 'warning' | 'info'> = {
  holiday: 'error',
  half_day_schedule: 'warning',
  alternate_saturday: 'info',
};

const OVERRIDE_TYPE_ICONS: Record<OverrideType, React.ReactNode> = {
  holiday: <HolidayIcon fontSize="small" />,
  half_day_schedule: <HalfDayIcon fontSize="small" />,
  alternate_saturday: <AltSatIcon fontSize="small" />,
};

function formatDateRange(start: string, end: string): string {
  if (start === end) return start;
  return `${start} – ${end}`;
}

function formatDayParity(override: ScheduleOverride): string {
  if (override.override_type === 'alternate_saturday') {
    const positions = override.working_saturday_positions;
    if (!positions || positions.length === 0) return '—';
    const ordinals = ['1st', '2nd', '3rd', '4th', '5th'];
    return positions.map(p => ordinals[p - 1]).join(', ') + ' Saturday';
  }
  if (override.override_type === 'half_day_schedule') {
    return override.day_of_week
      ? override.day_of_week === 'ALL'
        ? 'All days'
        : override.day_of_week.charAt(0) + override.day_of_week.slice(1).toLowerCase()
      : '—';
  }
  return '—';
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  schoolId: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ScheduleOverridesTab({ schoolId }: Props) {
  const queryClient = useQueryClient();
  const { role } = useAuthStore();
  const isAdmin = role === 'admin' || role === 'super_admin';

  const [formOpen, setFormOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<ScheduleOverride | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScheduleOverride | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successWarning, setSuccessWarning] = useState<string | null>(null);

  // Req 6.3: fetch overrides sorted by start_date ASC
  const { data: overrides = [], isLoading, error } = useQuery({
    queryKey: queryKeys.scheduleOverrides.list({ school_id: schoolId }),
    queryFn: () => getScheduleOverridesDirect(schoolId),
    enabled: !!schoolId,
  });

  // Req 6.4: delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteScheduleOverrideDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduleOverrides.all });
      setDeleteTarget(null);
    },
    onError: (err: Error) => {
      setDeleteError(err.message);
      setDeleteTarget(null);
    },
  });

  const handleOpenCreate = () => {
    setEditingOverride(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (override: ScheduleOverride) => {
    setEditingOverride(override);
    setFormOpen(true);
  };

  const handleFormSuccess = (override: ScheduleOverride, warning?: string) => {
    if (warning) setSuccessWarning(warning);
    setFormOpen(false);
    setEditingOverride(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Schedule Overrides</Typography>
          <Typography variant="body2" color="text.secondary">
            Holidays, half-day schedules, and alternate Saturday rules
          </Typography>
        </Box>
        {/* Req 6.6: only admins can create */}
        {isAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add Override
          </Button>
        )}
      </Box>

      {/* Req 6.5: out-of-academic-year warning from last save */}
      {successWarning && (
        <Alert severity="warning" onClose={() => setSuccessWarning(null)} sx={{ mb: 2 }}>
          {successWarning}
        </Alert>
      )}

      {deleteError && (
        <Alert severity="error" onClose={() => setDeleteError(null)} sx={{ mb: 2 }}>
          {deleteError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load overrides: {(error as Error).message}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : overrides.length === 0 ? (
        <Paper variant="outlined" sx={{ textAlign: 'center', py: 8, px: 4 }}>
          <AltSatIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No schedule overrides yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Add holidays, half-day schedules, or alternate Saturday rules to customise the school calendar.
          </Typography>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Add First Override
            </Button>
          )}
        </Paper>
      ) : (
        /* Req 6.3: table sorted by start_date ASC */
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date Range</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Day / Parity</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Alternate Schedule</TableCell>
                {isAdmin && <TableCell sx={{ fontWeight: 700, width: 96 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {overrides.map((override) => (
                <OverrideRow
                  key={override.id}
                  override={override}
                  isAdmin={isAdmin}
                  onEdit={() => handleOpenEdit(override)}
                  onDelete={() => setDeleteTarget(override)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit dialog */}
      <ScheduleOverrideFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingOverride(null); }}
        schoolId={schoolId}
        override={editingOverride}
        onSuccess={(override) => handleFormSuccess(override)}
      />

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Override"
        message={
          deleteTarget
            ? `Delete "${deleteTarget.name}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        severity="error"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}

// ── Row sub-component ─────────────────────────────────────────────────────────

interface OverrideRowProps {
  override: ScheduleOverride;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function OverrideRow({ override, isAdmin, onEdit, onDelete }: OverrideRowProps) {
  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {OVERRIDE_TYPE_ICONS[override.override_type]}
          <Typography variant="body2" fontWeight={600}>{override.name}</Typography>
        </Box>
      </TableCell>

      <TableCell>
        <Chip
          label={OVERRIDE_TYPE_LABELS[override.override_type]}
          color={OVERRIDE_TYPE_COLORS[override.override_type]}
          size="small"
          variant="outlined"
        />
      </TableCell>

      <TableCell>
        <Typography variant="body2">
          {formatDateRange(override.start_date, override.end_date)}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDayParity(override)}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {override.alternate_schedule_id ? (
            <Chip label="Assigned" size="small" variant="outlined" color="default" />
          ) : (
            '—'
          )}
        </Typography>
      </TableCell>

      {isAdmin && (
        <TableCell>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
    </TableRow>
  );
}
